/**
 * HTTP calls for the inbox. **Every `fetch` in the client lives under `data/`.**
 *
 * ┌─ ⛔ WHY LOCATION IS THE RULE RATHER THAN AN EXEMPTION LIST ─────────────────┐
 * │ `tests/web-query-registry.test.ts` fails on a `fetch(` anywhere in          │
 * │ `web/src` outside `data/`, because a call that does not go through          │
 * │ `subscribe()` is never refetched on reconnect - the missed-event failure    │
 * │ arriving through the fix for missed events.                                 │
 * │                                                                            │
 * │ The first version of `useConversations` called `fetch` in `inbox/` and the  │
 * │ scan caught it. **The fix was to move the call, not to exempt the file.**   │
 * │ An exemption would have been correct for that one hook and would have made  │
 * │ the next list hook want one too, and a list of exemptions long enough stops │
 * │ being a check.                                                              │
 * │                                                                            │
 * │ ⚠️ THE RESIDUAL IS NAMED: this makes `data/` the place a fetch is ALLOWED,  │
 * │ so a stray call hidden here is not caught. `data/` is small and its whole   │
 * │ job is this, which is the mitigation - not an argument that it cannot       │
 * │ happen.                                                                     │
 * └────────────────────────────────────────────────────────────────────────────┘
 */
import { markDataArrived } from '../inbox/list-rendered-mark.js'
import { reportUnauthorized } from './unauthorized.js'
import type { ConversationListRow } from '../../../src/services/conversation-list.js'
import type { PinnedRow, ThreadMessage } from '../../../src/services/thread.js'
import type { ConversationPanel } from '../../../src/services/conversation-panel.js'

export type ConversationsPage = {
  conversations: ConversationListRow[]
  cursor: string | null
}

export type ThreadPage = {
  messages: ThreadMessage[]
  /**
   * ⛔ THE PINNED ROWS, WHICH ARE NOT A SUBSET OF `messages`. A price agreed two
   * hundred messages ago is not on the loaded range, and the pinned region
   * exists so it is found WITHOUT scrolling - so the server sends it with every
   * page rather than the client filtering what it happens to hold.
   */
  pinned: PinnedRow[]
  cursor: string | null
}

export type PanelResponse = {
  /** `null` for absent, denied and malformed alike. One answer, by design. */
  panel: ConversationPanel | null
}

/**
 * Every inbox GET goes through here. **One request shape, one 401 report.**
 *
 * ┌─ ⛔ WHY A WRAPPER RATHER THAN TWO FUNCTIONS THAT EACH DO THE SAME THREE ────┐
 * │ THINGS. The list and the thread differ in exactly one character of URL, and │
 * │ the three things they share are the three that are silent when omitted:     │
 * │                                                                            │
 * │   `credentials`   omitted, the browser sends no cookie and the server       │
 * │                   answers 401 - which reads as an expired session.          │
 * │   the throw       omitted, a 500 returns `undefined` and renders as empty.  │
 * │   the 401 report  omitted, the session ends and NOTHING SAYS SO. The app    │
 * │                   keeps showing stale rows and every later request fails.   │
 * │                                                                            │
 * │ ⚠️ THE THIRD IS THE ONE THAT CANNOT BE NOTICED. A missing cookie fails      │
 * │ immediately and a missing throw shows an empty list; a missing report shows │
 * │ a WORKING INBOX with data nobody can refresh.                               │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ AND THE RESIDUAL IS THE SAME ONE THE DIRECTORY HAS: a fetch written inside
 * `data/` that skips this wrapper is not caught by anything. `data/` is small
 * and its whole job is this, which is the mitigation rather than an argument
 * that it cannot happen.
 */
async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(path, {
    // The session cookie is httpOnly, so the browser attaches it and JavaScript
    // never touches the token. `same-origin` is enough: Caddy serves the API and
    // the app from one origin.
    //
    // ⚠️ AND IT IS THE ONE LINE THAT BLOCKS A NATIVE CLIENT. Recorded as
    // `same-origin-blocks-a-native-client` in `ops/deferrals.txt`, owner
    // INTEGRATIONS. A native client has no browser cookie jar and nothing here
    // could carry a bearer token instead; FOUNDATION anticipated this on the
    // server side in `extractToken` and deliberately left the client unbuilt.
    credentials: 'same-origin',
  })

  /**
   * ⛔ REPORTED BEFORE THE THROW, AND BOTH HAPPEN. The report tells the app its
   * session is gone; the throw tells the caller this request has no answer.
   * Returning instead of throwing would hand the view an `undefined` page to
   * render, and reporting without throwing would leave a hook waiting forever.
   *
   * ⚠️ `reportUnauthorized` DOES NOT DECIDE THAT THE SESSION EXPIRED - it says a
   * request was refused. A cold load with no cookie produces the identical 401
   * and is somebody who was never signed in. `App.tsx` holds the user and makes
   * that call; see `data/unauthorized.ts`.
   */
  if (res.status === 401) {
    reportUnauthorized()
    throw new Error('HTTP 401')
  }

  // ⛔ THROWS RATHER THAN RETURNING AN EMPTY PAGE. An empty page on a 500 is
  // indistinguishable from an empty inbox, which is the exact confusion the
  // list's own null-versus-[] distinction exists to prevent.
  if (!res.ok) {
    /**
     * ⛔ THE CODE GOES TO A DEVELOPER, NOT TO THE AGENT'S LINE. The three feed
     * error states said "Could not load this conversation. HTTP 502" until
     * 2026-08-11, and `HTTP 502` is nothing an agent can act on.
     *
     * ⚠️ AND IT IS MORE USEFUL HERE THAN IT WAS THERE: the status alone is
     * barely diagnostic, so this carries the PATH with it. A 502 on
     * `/api/inbox/conversations` and a 502 on `/panel` send an engineer to
     * different places.
     */
    console.error(`inbox request failed: ${res.status} ${path}`)
    throw new Error(`HTTP ${res.status}`)
  }
  return (await res.json()) as T
}

export async function fetchConversations(view: string, before?: string): Promise<ConversationsPage> {
  const params = new URLSearchParams({ view })
  // ⚠️ OMITTED RATHER THAN SENT EMPTY. `before=` is a different request from no
  // `before` at all, and the route reads `request.query.before ?? null`.
  if (before !== undefined) params.set('before', before)
  const page = await getJson<ConversationsPage>(`/api/inbox/conversations?${params.toString()}`)
  /**
   * ⛔ MEASURED HERE BECAUSE THE ALTERNATIVE WAS SUBTRACTION AND SUBTRACTION WAS
   * WRONG. The 2026-08-07 report derived the render cost as
   * `listRendered - (apiStart + apiDuration)` and reported 3-8 ms. That assumes
   * ZERO gap between the response landing on the network and React being
   * scheduled, and there is a microtask hop plus React's scheduler in between,
   * so the figure was under-attributed by an unknown amount. This is the actual
   * instant the data existed in the client.
   */
  markDataArrived()
  return page
}

/**
 * The first `pages` pages of a view, concatenated into one page-shaped result.
 *
 * ┌─ ⛔ WHY PAGINATION IS FETCHED THIS WAY AND NOT ACCUMULATED IN THE HOOK ────┐
 * │ 2026-08-20. The list stopped at 50 rows - roughly an hour of history on    │
 * │ this corpus - because the client never sent `before`. The route has        │
 * │ supported the cursor since it was written; nothing asked.                  │
 * │                                                                           │
 * │ The obvious fix is to keep page 1 in the query store and append later      │
 * │ pages in component state. **That quietly breaks the store's guarantee**:   │
 * │ `refetchAll()` on reconnect would refresh only the head, leaving pages 2..n│
 * │ frozen at whatever they said before the disconnection - stale rows stitched│
 * │ under fresh ones, which is worse than either alone because nothing marks   │
 * │ the seam.                                                                  │
 * │                                                                           │
 * │ ⚠️ SO THE WHOLE LOADED RANGE IS ONE QUERY. A reconnect refetches every     │
 * │ page the agent has actually scrolled to, and the store's refcount and      │
 * │ refetch set keep meaning what they say.                                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⛔ `MAX_PAGES` IS A REAL CEILING, NOT A GUESS AT ONE. Without it a long scroll
 * makes every reconnect re-request everything ever scrolled past, so the cost of
 * a reconnect grows with the session. At the route's 50-row page this is 1,000
 * rows, and the list is virtualised by nothing - so the DOM is the binding
 * constraint well before the network is.
 */
export const MAX_PAGES = 20

export async function fetchConversationPages(view: string, pages: number): Promise<ConversationsPage> {
  const wanted = Math.min(Math.max(1, pages), MAX_PAGES)
  const first = await fetchConversations(view)
  const all = [...first.conversations]
  let cursor = first.cursor

  for (let i = 1; i < wanted && cursor !== null; i += 1) {
    // ⚠️ SEQUENTIAL, BECAUSE EACH CURSOR COMES OUT OF THE PREVIOUS ANSWER. A
    // keyset cursor cannot be predicted, so there is nothing to parallelise.
    const next = await fetchConversations(view, cursor)
    all.push(...next.conversations)
    cursor = next.cursor
  }
  return { conversations: all, cursor }
}

/**
 * The note lifecycle. **Item 9's last bullet, finally.**
 *
 * ⛔ ADDRESSED BY NOTE ID ALONE. A note belongs to exactly one conversation, so
 * a URL carrying both invites the two to disagree and forces the route to pick
 * a winner - see `src/routes/inbox.ts`.
 *
 * ⚠️ 204 AND NO BODY, so there is nothing to parse and nothing for a caller to
 * start depending on. The caller refetches the thread, which is the same path a
 * reconnect takes and therefore the one that is already exercised.
 */
/**
 * ┌─ ⛔ THE CONTENT TYPE GOES ON REQUESTS THAT HAVE A BODY, AND ONLY THOSE ─────┐
 * │ Amir, 2026-09-12: *"NOTE DELETE DOES NOT WORK."* It never worked.          │
 * │                                                                            │
 * │ This helper set `content-type: application/json` on every call it made,     │
 * │ including `DELETE`, which sends no body. **Fastify refuses that pair**:     │
 * │                                                                            │
 * │   400 "Body cannot be empty when content-type is set to 'application/json'" │
 * │                                                                            │
 * │ ⛔ AND IT IS REFUSED BEFORE `preHandler`, so the route's own auth never ran  │
 * │ either. MEASURED by injecting both forms: with the header 400, without it   │
 * │ 401 - the second proving the request otherwise reaches the handler.         │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ EVERY HALF WAS ASSERTED AND THE SEAM BETWEEN THEM WAS NOT. `note-write.test.ts`
 * calls `deleteNote()` directly and passes; `ThreadView.test.tsx` renders the
 * tombstone and passes; **nothing sent the HTTP request the client actually
 * sends.** That is the class this project keeps recording, and it is why the
 * assertion added with this fix issues the real request rather than calling past
 * it.
 */
async function noteWrite(noteId: string, init: RequestInit): Promise<void> {
  const res = await fetch(`/api/inbox/notes/${encodeURIComponent(noteId)}`, {
    credentials: 'same-origin',
    ...(init.body === undefined ? {} : { headers: { 'content-type': 'application/json' } }),
    ...init,
  })
  if (!res.ok) {
    console.error(`inbox request failed: ${res.status} /api/inbox/notes/${noteId}`)
    throw new Error(`HTTP ${res.status}`)
  }
}

export const editNoteApi = (noteId: string, body: string): Promise<void> =>
  noteWrite(noteId, { method: 'PATCH', body: JSON.stringify({ body }) })

export const pinNoteApi = (noteId: string, pinned: boolean): Promise<void> =>
  noteWrite(noteId, { method: 'PATCH', body: JSON.stringify({ pinned }) })

export const deleteNoteApi = (noteId: string): Promise<void> =>
  noteWrite(noteId, { method: 'DELETE' })

/**
 * One page of a thread, newest first.
 *
 * ⛔ NO `markDataArrived()` HERE, AND THAT IS NOT AN OVERSIGHT. The cold-load
 * target is *"the conversation list rendered and scrollable"*, and the mark is
 * that measurement's only honest instrument. A second caller would make
 * whichever request landed last define the moment "the data arrived", so the
 * number would silently start describing a different event.
 *
 * ⚠️ THE THREAD DESERVES ITS OWN MEASUREMENT AND DOES NOT YET HAVE ONE. Sharing
 * the list's would be the cheaper thing and it would corrupt the figure this
 * phase has already reported twice.
 */
export async function fetchThread(conversationId: string, before?: string): Promise<ThreadPage> {
  /**
   * ⚠️ ENCODED EVEN THOUGH THE CALLER ONLY EVER PASSES A UUID. `parseRoute`
   * guards the shape on the way in, and the day something else calls this - a
   * search result, a deep link handler, a notification payload - the guard is
   * somewhere else and this line is the one that is still here.
   */
  const q = before === undefined ? '' : `?before=${encodeURIComponent(before)}`
  return getJson<ThreadPage>(
    `/api/inbox/conversations/${encodeURIComponent(conversationId)}/messages${q}`,
  )
}

/**
 * The first `pages` pages of a thread, newest first, concatenated.
 *
 * ⛔ INSPECTION FINDING 15, AND IT IS FINDING 2 AGAIN RATHER THAN A SEPARATE
 * QUESTION. The brief asked whether thread pagination is efficient; the answer
 * is that there was none. `useThread` called `fetchThread` with no cursor and
 * dropped `page.cursor`, exactly as `useConversations` did - so a thread showed
 * its newest 50 messages and stopped, with nothing saying so.
 *
 * ⚠️ THE CURSOR IS OPAQUE HERE ON PURPOSE. The route encodes it as
 * `<iso>|<seq>` and `parseThreadCursor` owns that shape; a client that split it
 * would be a second place that knows the format, and `src/routes/inbox.ts` says
 * so at the parser. This passes back whatever it was handed.
 *
 * ⛔ SAME ONE-QUERY RULE AS THE LIST. See `fetchConversationPages` for why the
 * whole loaded range is a single store entry rather than a head plus component
 * state: a reconnect must refresh what is on screen, all of it.
 */
export async function fetchThreadPages(conversationId: string, pages: number): Promise<ThreadPage> {
  const wanted = Math.min(Math.max(1, pages), MAX_PAGES)
  const first = await fetchThread(conversationId)
  const all = [...first.messages]
  let cursor = first.cursor

  for (let i = 1; i < wanted && cursor !== null; i += 1) {
    const next = await fetchThread(conversationId, cursor)
    // ⚠️ APPENDED, NOT PREPENDED. Rows run newest-first and each page is older
    // than the last, so the concatenation is already in order. `.thread` is
    // `column-reverse`, which is what puts the oldest at the visual top.
    all.push(...next.messages)
    cursor = next.cursor
  }
  return { messages: all, pinned: first.pinned, cursor }
}

/**
 * The right panel: identity, notes, activity.
 *
 * ⛔ `panel` IS `null` FOR ABSENT **AND** FOR DENIED, and the client must not
 * try to tell them apart. The service joins the scope inside the identity query,
 * so both produce no row through one code path - see
 * `services/conversation-panel.ts`. A client branch that treated them
 * differently would rebuild the enumeration oracle on this side of the wire.
 */
/**
 * Compose a message or a note.
 *
 * ┌─ ⛔ THE KEY IS MINTED BY THE CALLER, AT COMMIT, AND NOT HERE ───────────────┐
 * │ Minting it inside this function would produce a NEW key on every call -     │
 * │ including a retry - which is the one thing the mechanism must not do. The   │
 * │ key identifies the INTENT, and only the caller knows when an intent began.  │
 * │                                                                            │
 * │ ⚠️ A key minted per composer-open would be wrong the other way: an agent    │
 * │ deliberately sending twice would get one message. Per COMMIT is the point   │
 * │ that distinguishes a retry from a second ask.                               │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ NOT ROUTED THROUGH `subscribe()`, and that is not an exemption to argue
 * about: the query store is for things a reconnect should REFETCH. Re-running a
 * write on reconnect would send the message again, which is why
 * `web-query-registry.test.ts` exempts `auth/api.ts` on the identical grounds -
 * "these are COMMANDS whose result is a transition rather than a rendered view".
 */
export async function postComposed(
  conversationId: string,
  what: 'messages' | 'notes',
  body: string,
  idempotencyKey: string,
): Promise<{ messageId: string }> {
  const res = await fetch(
    `/api/inbox/conversations/${encodeURIComponent(conversationId)}/${what}`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ body, idempotencyKey }),
    },
  )
  if (res.status === 401) {
    reportUnauthorized()
    throw new Error('HTTP 401')
  }
  // ⛔ THROWS SO THE COMPOSER CAN RESTORE THE TEXT. A write that fails silently
  // loses what somebody wrote, which is the only part of this a person cannot
  // reproduce.
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return (await res.json()) as { messageId: string }
}

export async function fetchPanel(conversationId: string): Promise<PanelResponse> {
  return getJson<PanelResponse>(
    `/api/inbox/conversations/${encodeURIComponent(conversationId)}/panel`,
  )
}

export type ContactThreadResponse = {
  contactId: string | null
  conversationId: string | null
  /** ⚠️ How many threads this contact has. Nothing renders it yet - see
   * `contact-with-several-threads-shows-only-one`. */
  threadCount: number
}

/**
 * ⛔ WHICH THREAD `/inbox/contacts/<n>` OPENS, ANSWERED BY THE SERVER.
 *
 * The client does not pick "the most recent" from a page of conversations,
 * because that would be a second implementation of the list's ordering - which
 * is exactly what Amir's ruling of 2026-09-11 forbids. The server calls
 * `conversationOrder`, the same function the list calls.
 */
export async function fetchContactThread(reference: number): Promise<ContactThreadResponse> {
  return getJson<ContactThreadResponse>(`/api/inbox/contacts/${encodeURIComponent(reference)}`)
}

/**
 * ⛔ AND THE SAME FOR `/inbox/bookings/<n>`. CRM-BUILD item 38's booking half.
 *
 * ⚠️ THE SERVER ANSWERS IT WITH THE SAME `conversationOrder`, WITH NO EXCEPTION -
 * Amir 2026-09-12. The tempting alternative, *"the thread that already has this
 * booking active"*, is a second rule for picking a conversation and this phase
 * has already paid for having two.
 *
 * ⛔ AND IT WRITES NOTHING. Opening this URL shows the booking in the panel for
 * THIS viewer; the shared pointer moves on an explicit "Make active" and
 * nowhere else. §3.7.
 */
export async function fetchBookingThread(reference: number): Promise<ContactThreadResponse> {
  const r = await getJson<{ thread: ContactThreadResponse | null }>(
    `/api/inbox/bookings/${encodeURIComponent(reference)}`,
  )
  /** ⚠️ The uniform miss flattened for the caller: an absent booking, one this
   *  scope cannot see and a malformed reference all arrive as `null`, and the
   *  resolver treats them the way it treats a contact with no thread. */
  return r.thread ?? { contactId: '', conversationId: null, threadCount: 0 }
}

/**
 * ⛔ "MAKE ACTIVE" - the only thing that moves the conversation's shared booking
 * pointer. A POST rather than a side effect of the GET, because an unfurl of a
 * pasted link would otherwise re-point the right panel of every colleague
 * reading that thread.
 */
export async function setActiveBookingApi(
  conversationId: string,
  bookingId: string | null,
): Promise<void> {
  /** ⚠️ Through `postCommand`, which is the helper the four header writes use -
   *  a 404 stays distinguishable there, so a control acting on a conversation
   *  somebody else has closed can say so rather than reading as a network
   *  fault. This is the fifth write of exactly that shape. */
  await postCommand<{ activeBookingId: string | null }>(
    `/api/inbox/conversations/${encodeURIComponent(conversationId)}/active-booking`,
    { bookingId },
  )
}

/**
 * ┌─ ⛔ ONE HELPER FOR THE FOUR HEADER WRITES, AND IT IS NOT `postComposed` ────┐
 * │ WITH A DIFFERENT URL. The composer's version throws on every failure so the │
 * │ composer can RESTORE THE TEXT - the one thing a person cannot reproduce.    │
 * │ These carry no text: what they need is for a 404 to be distinguishable, so  │
 * │ a control acting on a conversation that has been closed by somebody else,   │
 * │ or has left this agent's scope, can say so instead of reading as a network  │
 * │ fault.                                                                      │
 * │                                                                            │
 * │ ⚠️ SAME 401 REPORT AS EVERY OTHER CALL. Omitting it is the failure recorded │
 * │ at `getJson`: a session that has ended and NOTHING SAYING SO.               │
 * └────────────────────────────────────────────────────────────────────────────┘
 */
export class ConversationGone extends Error {
  constructor() {
    super('conversation not found')
    this.name = 'ConversationGone'
  }
}

async function postCommand<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  })
  if (res.status === 401) {
    reportUnauthorized()
    throw new Error('HTTP 401')
  }
  if (res.status === 404) throw new ConversationGone()
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return (await res.json()) as T
}

export type StatusResult = {
  conversationId: string
  status: 'open' | 'answered' | 'closed'
  events: Array<{ eventId: string; surface: string; systemMessageId: string | null }>
}

export async function setStatus(
  conversationId: string,
  to: 'open' | 'answered' | 'closed',
): Promise<StatusResult> {
  return postCommand<StatusResult>(
    `/api/inbox/conversations/${encodeURIComponent(conversationId)}/status`,
    { to },
  )
}

export type AssigneeResult = {
  conversationId: string
  assignedUserId: string | null
  eventId: string | null
}

/**
 * ⚠️ `null` IS UNASSIGN AND THE KEY IS ALWAYS SENT. The route refuses a body
 * with no `userId` key at all, because `{}` and `{"userId": null}` mean "you
 * forgot to say" and "nobody holds this" - and it will not treat the first as
 * the second.
 */
export async function setAssignee(
  conversationId: string,
  userId: string | null,
): Promise<AssigneeResult> {
  return postCommand<AssigneeResult>(
    `/api/inbox/conversations/${encodeURIComponent(conversationId)}/assignee`,
    { userId },
  )
}

/**
 * ⛔ RE-EXPORTED, NOT REDECLARED. Changed 2026-08-18 by the code audit's D2-1.
 *
 * Four types - `SnoozePreset`, `AssignableUser`, `ContactHit`, `MessageHit` -
 * were declared on BOTH sides of this seam and imported on NEITHER, while their
 * three siblings (`ConversationListRow`, `ThreadMessage`, `ConversationPanel`)
 * crossed by import and were therefore closed by `tsc`. All four agreed
 * field-for-field on the day they were measured, and nothing asserted that they
 * would go on agreeing.
 *
 * ⚠️ The re-export keeps every existing consumer's import path working: callers
 * still write `from '../data/inbox-api.js'`, and the definition now has one home.
 */
import type { SnoozePreset } from '../../../src/services/snooze.js'
import type { AssignableUser } from '../../../src/services/assignable-users.js'
import type { ContactHit } from '../../../src/services/contact-search.js'
import type { MessageHit } from '../../../src/services/message-search.js'
export type { SnoozePreset, AssignableUser, ContactHit, MessageHit }

export async function setSnooze(
  conversationId: string,
  when: { preset: SnoozePreset } | { at: string },
  note: string | null,
): Promise<{ snoozeUntil: string }> {
  return postCommand<{ snoozeUntil: string }>(
    `/api/inbox/conversations/${encodeURIComponent(conversationId)}/snooze`,
    { ...when, note },
  )
}

/** ⚠️ DELETE, so it does not go through `postCommand`. Same three outcomes. */
export async function cancelSnooze(conversationId: string): Promise<void> {
  const res = await fetch(
    `/api/inbox/conversations/${encodeURIComponent(conversationId)}/snooze`,
    { method: 'DELETE', credentials: 'same-origin' },
  )
  if (res.status === 401) {
    reportUnauthorized()
    throw new Error('HTTP 401')
  }
  if (res.status === 404) throw new ConversationGone()
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
}


export async function fetchAssignableUsers(): Promise<{ users: AssignableUser[] }> {
  return getJson<{ users: AssignableUser[] }>('/api/inbox/assignable-users')
}


/**
 * ⚠️ NOT ROUTED THROUGH `subscribe()`, and the reason is not the one that
 * exempts the writes. This is a READ, so re-running it on reconnect would be
 * harmless - it is exempt because its input is a query somebody is typing, and
 * a refetch on reconnect would repaint a search they have already closed. The
 * query store is for views that persist; the palette does not.
 */
export async function searchContactsApi(q: string): Promise<{ contacts: ContactHit[] }> {
  return getJson<{ contacts: ContactHit[] }>(
    `/api/inbox/search/contacts?q=${encodeURIComponent(q)}`,
  )
}


/**
 * ⚠️ THE FACETS ARE SPREAD INTO THE QUERY STRING INDIVIDUALLY, not serialised
 * as an object. The route reads them one at a time for the same reason - a
 * facet bag taken wholesale from the caller is a caller-supplied predicate set,
 * and the only thing separating that from a caller-supplied scope is which
 * column it names.
 */
export async function searchMessagesApi(
  q: string,
  facets: { channel?: string; assignee?: string; after?: string; before?: string } = {},
  cursor?: string,
): Promise<{ messages: MessageHit[]; cursor: string | null }> {
  const params = new URLSearchParams({ q })
  for (const [k, v] of Object.entries(facets)) if (v !== undefined && v !== '') params.set(k, v)
  if (cursor !== undefined) params.set('cursor', cursor)
  return getJson<{ messages: MessageHit[]; cursor: string | null }>(
    `/api/inbox/search/messages?${params.toString()}`,
  )
}
